// The layout AS DRAWN. A `GeneratedLayout` is a contract, not a picture: `layoutToPog` uses a segment's `xCm` only to pick a bay
// and an order, then re-derives every final x, so two contracts can differ where the screen does not. This runs the real
// `layoutToPog` and reads back with `pogToStudioItems`. Build `renderedProjectionOf` once per plan: it clones on every call.
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import type {
  GeneratedLayout,
  GeneratedPlacement,
  LayoutProjection,
  StudioItem,
} from "../domain/studioTypes";
import { layoutToPog } from "./layoutToPog";
import { pogToStudioItems } from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import { shelfLayoutItemsOf } from "../domain/shelfRankSpace";

export const renderedLayoutOf = (
  basePogs: readonly Pog[],
  layout: GeneratedLayout,
  items: readonly StudioItem[],
): GeneratedLayout => {
  const drawn = basePogs.map((pog, runIndex) => layoutToPog(pog, layout, items, runIndex));
  const placements: Record<string, GeneratedPlacement> = {};
  // Hung products are drawn where they were read; like the contract, the picture is shelves only.
  for (const item of shelfLayoutItemsOf(pogToStudioItems(drawn).items)) {
    placements[item.skuId] = {
      skuId: item.skuId,
      segments: item.placements.map((placement) => ({
        run: placement.run,
        shelf: placement.shelf,
        xCm: placement.xCm,
        hFacings: placement.hFacings,
      })),
    };
  }
  return { ...layout, placements };
};

export const renderedProjectionOf =
  (basePogs: readonly Pog[], items: readonly StudioItem[]): LayoutProjection =>
  (layout) =>
    renderedLayoutOf(basePogs, layout, items);
